import type { LayerSpecification, StyleSpecification } from "maplibre-gl";
import { classifyLayer, COMPONENT_ORDER, layerName, titleize } from "./classify";
import { findColors, normalizeColor } from "./colors";
import { friendlyName } from "./labels";
import { roleOf, zoomSideOf } from "./paths";
import type {
  ColorPath,
  ComponentId,
  Role,
  SimpleComponent,
  SimpleModel,
  Slot,
} from "./types";


function colorProps(layer: LayerSpecification): [string, unknown][] {
  const paint = (layer as { paint?: { [prop: string]: unknown } }).paint || {};
  return Object.keys(paint)
    .filter((prop) => prop.endsWith("-color"))
    .map((prop) => [prop, paint[prop]]);
}

function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function makeKey(parts: string[], taken: Set<string>): string {
  // "water.water.water" -> "water"
  const deduped = parts.filter((part, i) => part && part !== parts[i - 1]);
  const base = deduped.join(".");
  let key = base;
  for (let n = 2; taken.has(key); n++) key = `${base}_${n}`;
  taken.add(key);
  return key;
}

// "Rivers", "Streams and Rivers", "Ramps, Motorways and Main roads",
// "Ramps, Motorways and 3 more".
function makeLabel(names: string[]): string {
  if (names.length <= 1) return names[0] || "";
  if (names.length <= 3) {
    return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  }
  return `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;
}

// The name of the place a colour sits, as derived from the style: a layer
// name ("Minor") or a match label ("Grassland"). Stable, so slot keys built
// from it do not change when display names are reworded.
function rawName(layerId: string, context: string | undefined): string {
  if (!context || context.startsWith("zoom ")) return layerName(layerId);
  return titleize(context);
}

// What the user reads: "Small roads", and "Small roads (far)" or
// "Small roads (close)" for an end of a zoom blend.
function displayName(
  raw: string,
  section: string,
  context: string | undefined,
  zoomSide: "out" | "in" | null
): string {
  const name = friendlyName(raw, section);
  if (zoomSide) return `${name} (${zoomSide === "out" ? "far" : "close"})`;
  if (context?.startsWith("zoom ")) return `${name} (from ${context})`;
  return name;
}

type Group = {
  component: ComponentId;
  section: string;
  role: Role;
  // The first raw name, for the slot key.
  keyName: string;
  names: string[];
  paths: ColorPath[];
};

// Builds the Simple-mode view of a style: components, and inside each one a
// slot per distinct colour. Colours are merged by value within a section and
// role, so a slot is every place that colour is used there.
export function buildModel(style: StyleSpecification): SimpleModel {
  const layerIds = new Map<ComponentId, string[]>();
  const groups = new Map<string, Group>();

  (style.layers || []).forEach((layer) => {
    const { component, section } = classifyLayer(layer);
    layerIds.set(component, [...(layerIds.get(component) || []), layer.id]);

    colorProps(layer).forEach(([prop, value]) => {
      const role = roleOf(prop);
      findColors(value).forEach((occurrence) => {
        const color = normalizeColor(occurrence.color);
        const groupKey = [component, section, role, color].join("|");
        let group = groups.get(groupKey);
        if (!group) {
          group = {
            component,
            section,
            role,
            keyName: rawName(layer.id, occurrence.context),
            names: [],
            paths: [],
          };
          groups.set(groupKey, group);
        }
        const path = { layerId: layer.id, prop, path: occurrence.path };
        const name = displayName(
          rawName(layer.id, occurrence.context),
          section,
          occurrence.context,
          zoomSideOf(style, path)
        );
        if (!group.names.includes(name)) group.names.push(name);
        group.paths.push(path);
      });
    });
  });

  const taken = new Set<string>();
  const slots: Slot[] = Array.from(groups.values()).map((group) => ({
    key: makeKey(
      [
        group.component,
        group.section,
        slug(group.keyName),
        group.role === "shape" ? "" : group.role,
      ],
      taken
    ),
    component: group.component,
    section: group.section,
    role: group.role,
    label: makeLabel(group.names),
    paths: group.paths,
  }));

  const components: SimpleComponent[] = COMPONENT_ORDER.filter((id) =>
    layerIds.has(id)
  ).map((id) => ({
    id,
    layerIds: layerIds.get(id)!,
    slots: slots.filter((slot) => slot.component === id),
  }));

  return { components, slots };
}

// Changes when layers or the places colours live change, but not when a
// colour value changes. Used to decide whether a model is still usable.
export function structureSignature(style: StyleSpecification): string {
  return JSON.stringify(
    (style.layers || []).map((layer) => [
      layer.id,
      layer.type,
      (layer as { "source-layer"?: string })["source-layer"] || "",
      colorProps(layer).map(([prop, value]) => [
        prop,
        findColors(value).map((occurrence) => occurrence.path),
      ]),
    ])
  );
}
